<template>
  <div
    class="tw-rounded-2xl tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-100 dark:tw-border-slate-700 tw-shadow tw-py-3.5 tw-flex tw-flex-col tw-gap-3"
  >
    <div
      class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-body tw-font-medium tw-px-4"
    >
      <div class="tw-flex tw-justify-between tw-items-center tw-gap-3">
        <div class="tw-w-full tw-max-w-52 tw-flex tw-gap-2 tw-items-center">
          <i class="pi pi-search tw-text-sm tw-text-slate-500" />
          <InputText
            class="tw-bg-none tw-bg-transparent tw-h-7 tw-w-full tw-border-none tw-outline-none focus:tw-border-none focus:tw-outline-none focus:tw-shadow-none tw-p-0 tw-text-sm tw-font-medium tw-font-body"
            :placeholder="`Search ${
              props.selectedShow === 'all' ? 'Shows' : 'Episodes'
            }`"
            v-model="tableFilters.global.value"
          />
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <Button
            icon="pi pi-sync tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
            class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
            @click="refreshData()"
          />
          <download-csv :data="csvData">
            <Button
              label="CSV"
              icon="pi pi-download tw-mr-1.5 tw-text-sm"
              class="tw-h-7 tw-shadow tw-w-[68px] tw-px-2 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm tw-font-medium tw-font-body"
            ></Button>
          </download-csv>
        </div>
      </div>
    </div>
    <div>
      <DataTable
        :value="tableData"
        :globalFilterFields="['show_id', 'episode_id', 'title']"
        :filters="tableFilters"
        :paginator="true"
        :rows="10"
        responsiveLayout="scroll"
        :loading="props.loading"
        class="tw-bg-none tw-bg-transparent podcaster-shows-table"
        table-class="tw-bg-none tw-bg-transparent"
        :row-class="() => 'tw-bg-none tw-bg-transparent'"
      >
        <Column field="title" header="Title" :sortable="true" />
        <Column field="downloadsRaw" :sortable="true" dataType="numeric">
          <template #header>
            <span class="tw-inline-flex tw-items-center tw-gap-1">
              Downloads
              <i
                v-if="props.isTrial"
                v-tooltip.top="
                  'Unique downloads generated by Jamx promotional units'
                "
                class="pi pi-info-circle tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-cursor-help"
              ></i>
            </span>
          </template>
          <template #body="{ data }">
            {{ data.downloadsDisplay }}
          </template>
        </Column>
        <Column
          v-if="props.isTrial"
          field="listenersRaw"
          header="Unique Listeners"
          :sortable="true"
          dataType="numeric"
        >
          <template #body="{ data }">
            {{ data.listenersDisplay }}
          </template>
        </Column>
        <Column
          v-if="!props.isTrial"
          field="ctrRaw"
          header="CTR"
          :sortable="true"
          dataType="numeric"
        >
          <template #body="{ data }">
            {{ data.ctrDisplay }}
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { computed, ref, defineProps, defineEmits } from "vue";
import { FilterMatchMode } from "primevue/api";

const props = defineProps(["loading", "selectedShow", "data", "isTrial"]);

const emit = defineEmits(["refreshData"]);

const tableFilters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const tableData = computed(() => {
  return props.data.map((item) => ({
    ...item,
    playsRaw: toNumber(item.plays),
    listenersRaw: toNumber(item.listeners),
    ctrRaw: toNumber(item.ctr),
    downloadsRaw: toNumber(item.downloads),
    playsDisplay: formatNumber(item.plays),
    listenersDisplay: formatNumber(item.listeners),
    ctrDisplay: formatCTR(item.ctr),
    downloadsDisplay:
      item.downloads == null ? "—" : formatNumber(item.downloads),
  }));
});

const csvData = computed(() => {
  return props.data.map((item) => {
    const row = {
      ...item,
      downloads: item.downloads == null ? "" : formatNumber(item.downloads),
      ...(props.isTrial
        ? { listeners: formatNumber(item.listeners) }
        : { plays: formatNumber(item.plays), ctr: formatCTR(item.ctr) }),
    };
    delete row.views;
    if (!props.isTrial) delete row.listeners;
    return row;
  });
});

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-US").format(num);
};

const toNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatCTR = (ctr) => {
  return Number(ctr).toFixed(3);
};

const refreshData = () => {
  // Implement refresh functionality here
  emit("refreshData");
};
</script>

<style>
.podcaster-shows-table .p-paginator-bottom {
  border-color: transparent !important;
}
.podcaster-shows-table .p-datatable-thead th {
  background-color: transparent !important;
}
.podcaster-shows-table .p-paginator {
  background-color: transparent !important;
  border-color: transparent !important;
}
.podcaster-shows-table td,
.podcaster-shows-table th {
  border-color: #f1f5f9 !important; /* tw-border-slate-100 */
}
.tw-dark .podcaster-shows-table td,
.tw-dark .podcaster-shows-table th {
  border-color: #334155 !important; /* dark:tw-border-slate-700 */
}
</style>
